import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { toPng } from "html-to-image";
import { playSuccessChime } from "@/lib/notify";
import { AppShell } from "@/components/AppShell";
import { StatusPill } from "@/components/StatusPill";
import { MarkdownLite } from "@/components/MarkdownLite";
import { DynoChart } from "@/components/DynoChart";
import { FileDiagnostics } from "@/components/FileDiagnostics";
import { OrderChat } from "@/components/OrderChat";
import { TicketDialog } from "@/components/TicketDialog";
import { ProgressStepper, stepFromStatus } from "@/components/ProgressStepper";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { fmtDateTime } from "@/lib/datetime";
import { ArrowLeft, Download, FileDown, Cpu, Sparkles, Loader2, Zap, FileText, Star, Gauge, Share2, AlertTriangle, Lock, Truck, Car, Cog, X, Clock } from "lucide-react";

const SOLUTION_CODE = { stage1: "STAGE1", fap_off: "FAPOFF", egr_off: "EGROFF", adblue_off: "ADBLUE", dtc_off: "DTC", o2_off: "O2", immo_off: "IMMO", popbang: "POPBANG", tcu_stage1: "TCUSTAGE1", dsg_tuning: "DSG", zf_gearbox: "ZF", airbag_crash: "CRASH", airbag_eeprom: "EEPROM", hybrid_reset: "HYBRID" };
const imperiumName = (order) => {
  const codes = (order.services || []).map((s) => SOLUTION_CODE[s] || String(s).toUpperCase().replace(/_/g, ""));
  return `IMPERIUM_${(codes.length ? codes : ["FICHIER"]).join("_")}_V${order.file_version || 1}.bin`;
};

export const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const { refresh: refreshAuth } = useAuth();
  const dynoRef = useRef(null);
  const celebratedRef = useRef(false);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportEcu, setReportEcu] = useState("");
  const [reportNote, setReportNote] = useState("");
  const [showProblem, setShowProblem] = useState(false);
  const [problemReason, setProblemReason] = useState("");
  const [submittingProblem, setSubmittingProblem] = useState(false);

  const submitProblem = async () => {
    setSubmittingProblem(true);
    try {
      await api.post(`/orders/${id}/report_problem`, { reason: problemReason });
      setOrder((o) => ({ ...o, defect_reported: true, defect_reason: problemReason }));
      setShowProblem(false);
      toast.success("Merci — notre atelier a été alerté et va vérifier le fichier.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSubmittingProblem(false);
    }
  };

  const submitReport = async () => {
    try {
      await api.post(`/orders/${id}/report_ecu`, { correct_ecu: reportEcu, note: reportNote });
      setOrder((o) => ({ ...o, ecu_report: { correct_ecu: reportEcu, note: reportNote } }));
      setShowReport(false);
      toast.success(t("od.reportThanks"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("od.error"));
    }
  };

  const shareDyno = async () => {
    if (!dynoRef.current) return;
    try {
      const dataUrl = await toPng(dynoRef.current, { backgroundColor: "#060606", pixelRatio: 2, cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `dyno-stage1-${order.id}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Stage 1 — Imperium", text: "Stage 1 🏁" });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl; a.download = file.name; a.click();
        toast.success(t("od.curveDownloaded"));
      }
    } catch (e) {
      toast.error(t("od.shareUnavail"));
    }
  };

  const submitReview = async () => {
    if (!rating) return toast.error(t("od.chooseRating"));
    setSubmittingReview(true);
    try {
      await api.post(`/orders/${id}/review`, { rating, comment });
      setOrder((o) => ({ ...o, review_rating: rating, review_comment: comment }));
      toast.success(t("od.reviewThanks"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("od.error"));
    } finally {
      setSubmittingReview(false);
    }
  };

  useEffect(() => {
    let timer;
    let cancelled = false;
    const fetchOrder = async (first = false) => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        if (cancelled) return;
        if (data.status === "completed" && !celebratedRef.current && !first) {
          celebratedRef.current = true;
          celebrate();
          toast.success(t("od.fileReady"));
        }
        if (data.status === "completed") celebratedRef.current = true;
        setOrder(data);
        if (data.analyzing) timer = setTimeout(() => fetchOrder(), 2500);
      } catch (e) {
        if (first) navigate("/client/orders");
      } finally {
        if (first) setLoading(false);
      }
    };
    fetchOrder(true);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [id, navigate]);

  const celebrate = () => {
    try { playSuccessChime(); } catch (e) {}
    const fire = (ratio, opts) => confetti({ ...opts, origin: { y: 0.6 }, particleCount: Math.floor(220 * ratio) });
    const colors = ["#10b981", "#34d399", "#a7f3d0", "#ffffff"];
    fire(0.25, { spread: 26, startVelocity: 55, colors });
    fire(0.2, { spread: 60, colors });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9, colors });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2, colors });
    fire(0.1, { spread: 120, startVelocity: 45, colors });
  };

  const download = async (which) => {
    try {
      const res = await api.get(`/orders/${id}/download/${which}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = which === "processed" ? imperiumName(order)
        : which === "secondary_processed" ? (order.secondary_processed_filename || "IMPERIUM_boite_mod.bin")
        : which === "secondary_original" ? (order.secondary_filename || "boite.bin")
        : order.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
      if (which === "processed" || which === "secondary_processed") {
        celebrate();
        try { const { data } = await api.get(`/orders/${id}`); setOrder(data); } catch (_) {}
        refreshAuth?.();
      }
    } catch (e) {
      let detail = e?.response?.data?.detail;
      if (e?.response?.status === 402 && e?.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail; } catch (_) {}
      }
      if (e?.response?.status === 402 && detail?.code === "insufficient_credits") {
        const sol = imperiumName(order).replace(/^IMPERIUM_/, "").replace(/\.bin$/, "");
        toast.error(t("od.missing", { n: detail.missing }));
        navigate(`/client/credits?need=${detail.missing}&unlock=${encodeURIComponent(sol)}`);
        return;
      }
      toast.error(t("od.dlImpossible"));
    }
  };

  const downloadCertificate = async () => {
    try {
      const res = await api.get(`/orders/${id}/certificate`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificat_${id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t("od.certUnavail"));
    }
  };

  if (loading) return <AppShell mode="client"><div className="font-mono text-xs text-zinc-500">{t("dash.loading")}</div></AppShell>;
  if (!order) return null;

  const fuelLabel = { essence: "Essence", diesel: "Diesel" };
  const info = [
    [t("od.brand"), order.vehicle_brand], [t("od.model"), order.vehicle_model], [t("od.year"), order.vehicle_year],
    [t("od.engine"), order.engine],
    [t("od.ecuDetected"), order.ecu_detected ? `${order.ecu_detected}${order.ecu_fuel ? " · " + (fuelLabel[order.ecu_fuel] || "") : ""}` : null],
    [t("od.ecuEntered"), order.ecu], [t("od.gearbox"), order.gearbox], [t("od.tool"), order.reading_tool],
  ].filter(([, v]) => v);

  return (
    <AppShell mode="client">
      <Link to="/client/orders" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 font-mono text-xs uppercase tracking-widest">
        <ArrowLeft className="w-4 h-4" /> {t("od.back")}
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="font-mono text-xs text-aurora-cyan tracking-[0.3em] uppercase mb-2">// {t("od.order")} {order.id.slice(0, 8)}</div>
          <h1 className="font-display text-4xl font-bold uppercase tracking-tight">{order.vehicle_brand} {order.vehicle_model}</h1>
          <div data-testid="order-datetime" className="font-mono text-[11px] text-zinc-500 mt-2 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {fmtDateTime(order.created_at, lang)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="vehicle-class-badge" className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm border flex items-center gap-1 ${order.vehicle_class === "truck_agri" ? "bg-[#FFB020]/10 text-[#FFB020] border-[#FFB020]/40" : "bg-aurora-cyan/10 text-aurora-cyan border-aurora-cyan/40"}`}>
            {order.vehicle_class === "truck_agri" ? <Truck className="w-3 h-3" /> : <Car className="w-3 h-3" />}
            {order.vehicle_class === "truck_agri" ? t("vc.truck") : t("vc.car")}
          </span>
          {order.auto_processed && (
            <span data-testid="auto-badge" className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm bg-signal-success/10 text-signal-success border border-signal-success/40 flex items-center gap-1">
              <Zap className="w-3 h-3" /> {t("od.autoInstant")}
            </span>
          )}
          {order.closed && (
            <span data-testid="client-closed-badge" className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm bg-zinc-500/15 text-zinc-300 border border-zinc-400/40 flex items-center gap-1">
              <Lock className="w-3 h-3" /> {t("od.closed")}
            </span>
          )}
          <StatusPill status={order.status} testid="detail-status" />
        </div>
      </div>

      {order.status !== "rejected" && (
        <div className="mb-6">
          <ProgressStepper
            step={order.progress_step ?? stepFromStatus(order.status)}
            note={order.progress_note}
          />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* AI report */}
          <div className="glass rounded-md p-6">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <Sparkles className="w-5 h-5 text-aurora-purple" />
              <h3 className="font-display text-lg font-semibold uppercase tracking-tight">{t("od.aiTitle")}</h3>
              {order.ecu_confidence != null && (
                <span data-testid="ai-confidence" className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-sm border flex items-center gap-1 ${order.ecu_confidence >= 85 ? "text-signal-success border-signal-success/40 bg-signal-success/10" : order.ecu_confidence >= 60 ? "text-aurora-cyan border-aurora-cyan/40 bg-aurora-cyan/10" : "text-signal-processing border-signal-processing/40 bg-signal-processing/10"}`}>
                  <Sparkles className="w-2.5 h-2.5" /> {t("od.reliable", { n: order.ecu_confidence })}
                </span>
              )}
            </div>
            {order.ai_analysis ? (
              <MarkdownLite text={order.ai_analysis} />
            ) : (
              <div className="flex items-center gap-2 text-zinc-400 font-mono text-xs py-6" data-testid="analysis-pending">
                <Loader2 className="w-4 h-4 animate-spin text-aurora-purple" /> {t("od.aiPending")}
              </div>
            )}
          </div>

          <OrderChat orderId={order.id} role="client" vehicle={`${order.vehicle_brand} ${order.vehicle_model}`} />

          <FileDiagnostics integrity={order.integrity} />


          {order.dyno && order.dyno.base_hp > 0 && (
            <div ref={dynoRef} className="glass rounded-md p-6" data-testid="dyno-card">
              <div className="flex items-center gap-2 mb-4">
                <Gauge className="w-5 h-5 text-aurora-cyan" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">{t("od.dynoTitle")}</h3>
                {order.dyno.source === "ai" && (
                  <span className="font-mono text-[9px] uppercase tracking-widest text-aurora-purple bg-aurora-purple/10 px-1.5 py-0.5 rounded-sm">{t("od.aiEstimate")}</span>
                )}
                <button data-testid="share-dyno-btn" onClick={shareDyno} className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-white/15 hover:border-aurora-cyan/60 font-mono text-[10px] uppercase tracking-widest text-zinc-300 transition-colors">
                  <Share2 className="w-3.5 h-3.5" /> {t("od.share")}
                </button>
              </div>
              <DynoChart baseHp={order.dyno.base_hp} gainHp={order.dyno.gain_hp} baseNm={order.dyno.base_nm} gainNm={order.dyno.gain_nm} />
              <p className="mt-3 font-mono text-[10px] text-zinc-600">{t("od.dynoNote")}</p>
            </div>
          )}

          {order.admin_notes && (
            <div className="glass rounded-md p-6 border-l-2 border-aurora-cyan">
              <h3 className="font-mono text-[10px] uppercase tracking-widest text-aurora-cyan mb-2">{t("od.engineerMsg")}</h3>
              <p className="text-sm text-zinc-300">{order.admin_notes}</p>
            </div>
          )}

          {order.status === "completed" && (
            <div className="glass rounded-md p-6" data-testid="review-card">
              <div className="flex items-center gap-2 mb-4">
                <Star className="w-5 h-5 text-signal-processing" />
                <h3 className="font-display text-lg font-semibold uppercase tracking-tight">{t("od.reviewTitle")}</h3>
              </div>
              {order.review_rating ? (
                <div>
                  <div className="flex mb-2">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`w-5 h-5 ${i <= order.review_rating ? "text-signal-processing fill-signal-processing" : "text-zinc-700"}`} />)}</div>
                  {order.review_comment && <p className="text-sm text-zinc-300">"{order.review_comment}"</p>}
                  <p className="font-mono text-[10px] text-signal-success mt-2 uppercase tracking-widest">{t("od.thanksReview")}</p>
                </div>
              ) : (
                <>
                  <div className="flex gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button key={i} data-testid={`star-${i}`} onClick={() => setRating(i)} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}>
                        <Star className={`w-7 h-7 transition-colors ${i <= (hover || rating) ? "text-signal-processing fill-signal-processing" : "text-zinc-700"}`} />
                      </button>
                    ))}
                  </div>
                  <textarea data-testid="review-comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("od.commentPh")} className="w-full bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none mb-3" />
                  <button data-testid="submit-review-btn" onClick={submitReview} disabled={submittingReview} className="flex items-center gap-2 px-5 py-2.5 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cyan-300 transition-colors disabled:opacity-60">
                    {submittingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} {t("od.publishReview")}
                  </button>
                </>
              )}

              {/* Report a problem on the delivered file */}
              <div className="mt-5 pt-4 border-t border-white/10">
                {order.defect_reported ? (
                  <div data-testid="problem-reported" className="flex items-center gap-2 text-signal-error font-mono text-[11px] uppercase tracking-widest">
                    <AlertTriangle className="w-4 h-4" /> Problème signalé — l'atelier vérifie le fichier
                  </div>
                ) : !showProblem ? (
                  <button data-testid="report-problem-btn" onClick={() => setShowProblem(true)}
                    className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-signal-error transition-colors">
                    <AlertTriangle className="w-3.5 h-3.5" /> Un problème avec ce fichier ? Signaler
                  </button>
                ) : (
                  <div className="space-y-2.5" data-testid="report-problem-form">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">Décrivez le problème rencontré (voyant, à-coups, démarrage…)</p>
                    <textarea data-testid="problem-reason" value={problemReason} onChange={(e) => setProblemReason(e.target.value)} rows={2}
                      placeholder="ex. voyant moteur allumé après flash" className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-signal-error focus:outline-none" />
                    <div className="flex gap-2">
                      <button data-testid="submit-problem-btn" onClick={submitProblem} disabled={submittingProblem}
                        className="flex items-center gap-1.5 px-4 py-2 bg-signal-error text-white font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:opacity-90 transition-opacity disabled:opacity-60">
                        {submittingProblem ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />} Signaler le problème
                      </button>
                      <button onClick={() => setShowProblem(false)} className="px-4 py-2 border border-white/15 text-zinc-400 font-mono text-[10px] uppercase tracking-widest rounded-sm hover:text-white transition-colors">Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Files */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">{t("od.filesTitle")}</h3>
            <button onClick={() => download("original")} data-testid="download-original-btn" className="w-full flex items-center gap-3 p-3 rounded-sm border border-white/10 hover:border-white/30 transition-colors mb-3">
              <FileDown className="w-5 h-5 text-zinc-400" />
              <div className="text-left min-w-0 flex-1">
                <div className="font-mono text-xs text-white truncate">{order.original_filename}</div>
                <div className="font-mono text-[10px] text-zinc-500">{t("od.originalFile")}</div>
              </div>
            </button>
            {order.processed_filename ? (
              <button onClick={() => download("processed")} data-testid="download-processed-btn"
                className="btn-flash-green group w-full flex items-center gap-3 p-4">
                <div className="w-11 h-11 grid place-items-center rounded-full bg-black/25 border border-black/20 shrink-0">
                  <Download className="w-6 h-6 text-[#05221a]" />
                </div>
                <div className="text-left min-w-0 flex-1">
                  <div className="font-display font-bold uppercase tracking-wide text-[#05221a] text-sm flex items-center gap-2">{t("od.downloadFile")} <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm bg-black/25 text-white">{t("od.ready")}</span></div>
                  <div className="font-mono text-[11px] text-[#062a20] font-semibold truncate mt-0.5">{imperiumName(order)}</div>
                  <div className="font-mono text-[10px] text-[#063a2c]">{t("od.processedReady", { v: order.file_version || 1 })}</div>
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-3 p-3 rounded-sm border border-dashed border-white/10">
                <Cpu className="w-5 h-5 text-zinc-600" />
                <div className="font-mono text-[10px] text-zinc-500">{t("od.processedWaiting")}</div>
              </div>
            )}

            {order.secondary_filename && (
              <div className="mt-4 pt-4 border-t border-white/10" data-testid="client-gearbox-card">
                <div className="font-mono text-[10px] uppercase tracking-widest text-aurora-purple mb-2 flex items-center gap-1.5"><Cog className="w-3.5 h-3.5" /> Boîte de vitesses</div>
                {order.secondary_cancelled ? (
                  <div className="flex items-center gap-2 p-3 rounded-sm border border-signal-error/30 bg-signal-error/10">
                    <X className="w-4 h-4 text-signal-error shrink-0" />
                    <div className="font-mono text-[11px] text-signal-error">Non réalisable — crédits remboursés</div>
                  </div>
                ) : order.secondary_processed_filename ? (
                  <button onClick={() => download("secondary_processed")} data-testid="download-secondary-btn"
                    className="w-full flex items-center gap-3 p-3 rounded-sm border border-aurora-purple/40 bg-aurora-purple/10 hover:bg-aurora-purple/20 transition-colors">
                    <Download className="w-5 h-5 text-aurora-purple shrink-0" />
                    <div className="text-left min-w-0">
                      <div className="font-display uppercase tracking-wide text-sm">Télécharger le fichier boîte</div>
                      <div className="font-mono text-[10px] text-zinc-400">{order.secondary_credits ? `${order.secondary_credits} crédit(s)` : ""}</div>
                    </div>
                  </button>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-sm border border-dashed border-white/10">
                    <Cog className="w-5 h-5 text-zinc-600" />
                    <div className="font-mono text-[10px] text-zinc-500">Fichier boîte en cours de préparation…</div>
                  </div>
                )}
              </div>
            )}
            {order.status === "completed" && (
              <button onClick={downloadCertificate} data-testid="download-certificate-btn" className="mt-3 w-full flex items-center gap-3 p-3 rounded-sm border border-aurora-purple/40 bg-aurora-purple/5 hover:bg-aurora-purple/10 transition-colors">
                <FileText className="w-5 h-5 text-aurora-purple" />
                <div className="text-left">
                  <div className="font-mono text-xs text-white">{t("od.certTitle")}</div>
                  <div className="font-mono text-[10px] text-aurora-purple">{t("od.certSub")}</div>
                </div>
              </button>
            )}
          </div>

          {/* Support / Ticket */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-1">{t("od.supportTitle")}</h3>
            <p className="font-mono text-[10px] text-zinc-500 mb-4">{t("od.supportSub")}</p>
            <TicketDialog orderId={order.id} vehicle={`${order.vehicle_brand} ${order.vehicle_model}`} onCreated={() => toast.success(t("od.ticketCreated"))} />
          </div>

          {/* Solutions */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">{t("od.solutions")} · {order.total_credits} cr.</h3>
            <div className="flex flex-wrap gap-2">
              {order.services.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border border-aurora-cyan/30 text-aurora-cyan rounded-sm">{s}</span>
              ))}
            </div>
          </div>

          {/* Vehicle */}
          <div className="glass rounded-md p-6">
            <h3 className="font-display text-lg font-semibold uppercase tracking-tight mb-4">{t("od.detailsTitle")}</h3>
            <div className="space-y-2.5">
              {info.map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{k}</span>
                  <span className="text-zinc-200">{v}</span>
                </div>
              ))}
              {order.customer_note && (
                <div className="pt-2 border-t border-white/5">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{t("od.note")}</span>
                  <p className="text-sm text-zinc-300 mt-1">{order.customer_note}</p>
                </div>
              )}
            </div>

            {/* ECU detection report */}
            <div className="mt-4 pt-4 border-t border-white/5">
              {order.ecu_report ? (
                <p className="font-mono text-[10px] text-signal-success uppercase tracking-widest flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> {t("od.reportSent")}{order.ecu_report.correct_ecu ? ` · ${order.ecu_report.correct_ecu}` : ""}
                </p>
              ) : !showReport ? (
                <button onClick={() => setShowReport(true)} data-testid="report-ecu-btn" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-aurora-cyan transition-colors flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> {t("od.reportEcuBtn")}
                </button>
              ) : (
                <div className="space-y-2.5" data-testid="report-ecu-form">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">{t("od.reportTitle")}</p>
                  <input data-testid="report-ecu-input" value={reportEcu} onChange={(e) => setReportEcu(e.target.value)} placeholder="ex: Continental SID310" className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none" />
                  <textarea value={reportNote} onChange={(e) => setReportNote(e.target.value)} rows={2} placeholder={t("od.reportNotePh")} className="w-full bg-black/50 border border-white/10 text-white px-3 py-2 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none" />
                  <div className="flex gap-2">
                    <button onClick={submitReport} data-testid="report-ecu-submit" className="px-4 py-2 bg-aurora-cyan text-black font-display font-bold uppercase tracking-wider text-xs rounded-sm">{t("od.send")}</button>
                    <button onClick={() => setShowReport(false)} className="px-4 py-2 border border-white/15 font-display font-bold uppercase tracking-wider text-xs rounded-sm hover:bg-white/5 transition-colors">{t("od.cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
};
